/**
 * Live cards, Admin side: the pure half of LiveUpdatesProvider (Warren,
 * 28 Oct 2026: "Every card and list in Admin and the driver app streams
 * live ... no polling, no refresh button").
 *
 * The Api's /admin/live stream sends an invalidation event per changed
 * document inside the caller's scope: { kind, id, tenant_id, location_id,
 * charger_id, user_id }, ids only. The provider batches them (at most one
 * refetch round per tab every 5 s) and invalidates the react-query queries whose
 * keys this table assigns to the kinds that arrived; react-query then
 * refetches the ones on screen through their ordinary routes.
 *
 * LIVE_QUERY_KEYS is the one place that says which screen reads which
 * kind. A new live screen adds its key pattern here; a key missing from the
 * table simply does not update live. Patterns are key prefixes, with ANY
 * matching one element (the dashboard keys carry the viewer identity at
 * position 1).
 *
 * Run the tests: `tsx --test src/lib/liveUpdates.test.ts` from apps/admin/.
 */

export const LIVE_KINDS = ['session', 'charger', 'payout', 'agent_payout', 'points'] as const;
export type LiveKind = typeof LIVE_KINDS[number];

export interface LiveEvent {
  kind: LiveKind;
  id: string;
  tenant_id: string | null;
  location_id: string | null;
  charger_id: string | null;
  user_id: string | null;
}

export const ANY = '*';
export type KeyPattern = readonly string[];

/**
 * Event kind to the query-key prefixes it makes stale. Built from the keys
 * the pages use (hooks/use*.ts, pages/**). Not here, on purpose: the
 * charger-uptime tile (heartbeat buckets), pending Stripe payouts, the
 * notification bell, the Sales book and partner job / quote / program
 * screens, and OCPP frame timelines. No event covers their data, so they
 * keep their own refresh. Also left out: the All-time revenue tile
 * (['dashboard', identity, 'stats']). GET /dashboard/stats sums every
 * session in scope, too heavy for a round every 5 s per tab, so it polls
 * every 60 s instead (hooks/useDashboardStats.ts).
 */
export const LIVE_QUERY_KEYS: Readonly<Record<LiveKind, readonly KeyPattern[]>> = {
  session: [
    // Dashboard tiles.
    ['dashboard', ANY, 'operator-summary'],
    // Recent sessions, and the Sessions widget's All and Active views
    // (['dashboard', identity, 'recent-sessions', 'active'] shares this
    // prefix; lib/sessionsViews.ts recentSessionsQuery).
    ['dashboard', ANY, 'recent-sessions'],
    ['dashboard', ANY, 'charger-utilization'],
    ['dashboard', 'per-location-revenue'],
    ['dashboard', ANY, 'session-trends'],
    ['dashboard', ANY, 'start-timeouts'],
    ['dashboard', 'top-revenue-locations'],
    // Location summary card, session lists and session detail.
    ['locations', ANY, 'summary'],
    ['sessions'],
    ['session'],
    ['charger', ANY, 'active-session'],
    // Driver profile, its session list, and the drivers list.
    ['admin-driver'],
    ['admin-drivers'],
    ['driver-sessions'],
    // Sales Partner pages.
    ['sp-dashboard', 'active-sessions'],
    ['sp-reports', 'sessions'],
    ['sp-reports', 'energy'],
    ['sp-reports', 'utilization'],
    // Site and fleet pages.
    ['site-comparison'],
    ['fleet-sessions'],
    ['fleet-stats'],
    ['fleet-drivers'],
  ],
  charger: [
    ['dashboard', ANY, 'operator-summary'],
    ['dashboard', ANY, 'anomalies'],
    ['dashboard', ANY, 'map-points'],
    ['dashboard', ANY, 'charger-utilization'],
    ['dashboard', ANY, 'start-timeouts'],
    ['locations', ANY, 'summary'],
    ['location-chargers'],
    ['sp-dashboard', 'chargers'],
    ['sp-dashboard', 'online-chargers'],
    ['sp-my-chargers', 'chargers'],
    ['sp-my-chargers', 'locations'],
    ['sp-reports', 'utilization'],
    ['partner-profile'],
    ['my-site'],
    ['fleet-chargers'],
    ['admin', 'fleet-chargers'],
  ],
  payout: [
    ['dashboard', 'settlement-summary'],
    ['session', ANY, 'payments'],
    ['admin-driver'],
    ['agent-reconciliation'],
    ['site-payments'],
  ],
  agent_payout: [
    ['sp-dashboard', 'payouts'],
    ['sp-reports', 'payouts'],
    ['sp-reports', 'payouts-tab'],
    ['agent-reconciliation'],
    ['partner-profile'],
    ['partner-history'],
  ],
  points: [
    ['admin-driver'],
    ['admin-drivers'],
    ['fleet-drivers'],
  ],
};

/** A key matches a pattern when it starts with it, ANY matching any element. */
export function keyMatches(pattern: KeyPattern, key: readonly unknown[]): boolean {
  if (key.length < pattern.length) return false;
  return pattern.every((p, i) => p === ANY || p === key[i]);
}

/** Whether a query key belongs to any of these kinds. */
export function keyIsLiveFor(key: readonly unknown[], kinds: Iterable<LiveKind>): boolean {
  for (const kind of kinds) {
    if (LIVE_QUERY_KEYS[kind].some((p) => keyMatches(p, key))) return true;
  }
  return false;
}

/** Parse one `live` event's data; null for anything malformed. */
export function parseLiveEvent(data: string): LiveEvent | null {
  try {
    const v = JSON.parse(data) as Partial<LiveEvent> | null;
    if (!v || typeof v !== 'object') return null;
    if (!LIVE_KINDS.includes(v.kind as LiveKind) || typeof v.id !== 'string') return null;
    const str = (x: unknown) => (typeof x === 'string' ? x : null);
    return {
      kind: v.kind as LiveKind,
      id: v.id,
      tenant_id: str(v.tenant_id),
      location_id: str(v.location_id),
      charger_id: str(v.charger_id),
      user_id: str(v.user_id),
    };
  } catch {
    return null;
  }
}

/**
 * A batch the provider hands on. `events: null` is a catch-up: everything
 * live is stale (the stream reconnected after a gap, or is down).
 */
export interface LiveBatch {
  kinds: ReadonlySet<LiveKind>;
  events: readonly LiveEvent[] | null;
}

export const ALL_KINDS: ReadonlySet<LiveKind> = new Set(LIVE_KINDS);

type TimerHandle = unknown;

/**
 * At most one flush per interval. The first event after a quiet spell
 * flushes at once; events arriving inside the interval wait for one
 * trailing flush at its end. A burst of fifty events is two refetch rounds,
 * and a single event is never delayed.
 */
export function createLiveBatcher(opts: {
  intervalMs: number;
  onFlush: (batch: LiveBatch) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => TimerHandle;
  clearTimer?: (h: TimerHandle) => void;
}): { add: (e: LiveEvent) => void; catchUp: () => void; dispose: () => void } {
  const now = opts.now ?? (() => Date.now());
  const setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  let pending: LiveEvent[] = [];
  let everything = false;
  let timer: TimerHandle | null = null;
  let lastFlush = Number.NEGATIVE_INFINITY;

  const flush = (): void => {
    timer = null;
    lastFlush = now();
    const batch: LiveBatch = everything
      ? { kinds: ALL_KINDS, events: null }
      : { kinds: new Set(pending.map((e) => e.kind)), events: pending };
    pending = [];
    everything = false;
    if (batch.kinds.size > 0) opts.onFlush(batch);
  };
  const schedule = (): void => {
    if (timer !== null) return;
    const wait = Math.max(0, lastFlush + opts.intervalMs - now());
    if (wait === 0) flush();
    else timer = setTimer(flush, wait);
  };

  return {
    add(e) {
      pending.push(e);
      schedule();
    },
    catchUp() {
      everything = true;
      schedule();
    },
    dispose() {
      if (timer !== null) clearTimer(timer);
      timer = null;
      pending = [];
      everything = false;
    },
  };
}

/** Whether a batch concerns a subscriber watching these kinds (and, optionally, these events). */
export function batchTouches(
  batch: LiveBatch,
  kinds: readonly LiveKind[],
  match?: (e: LiveEvent) => boolean,
): boolean {
  if (!kinds.some((k) => batch.kinds.has(k))) return false;
  if (batch.events === null) return true;
  return batch.events.some((e) => kinds.includes(e.kind) && (!match || match(e)));
}

/**
 * At most one refetch round per tab every 5 s (Warren, 28 Oct 2026): a busy
 * network moves the dashboard tiles in rounds, never once per event.
 */
export const LIVE_FLUSH_INTERVAL_MS = 5_000;

export const RECONNECT_INITIAL_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

/** 1 s, 2 s, 4 s ... 30 s. */
export function nextBackoffMs(current: number): number {
  return Math.min(Math.max(current, RECONNECT_INITIAL_MS) * 2, RECONNECT_MAX_MS);
}

/** The `exp` claim in ms, read without verification; null when unreadable. */
export function tokenExpiresAt(token: string): number | null {
  try {
    const seg = token.split('.')[1];
    if (!seg) return null;
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}
